<template>
  <div>
    <main-layout
      controlsBg="light"
      :breadcrumb="getBreadcrumb"
      :hasControlsBar="false">
      <div
        class="container margin-top-xl margin-left-l margin-right-l">
        <h1
          class="margin-bottom-xl">
          <chevron
            @click="goBack()"/>
          {{ $t('variables.taxonomy') }}
        </h1>
        <grid-row>
          <grid-col
            class="margin-right-l"
            col-size="1">
            <div>
              <h2
                class="margin-bottom-l">
                <font-awesome-icon
                  class="icon"
                  :icon="icon.tags"/>
                {{ $t('variables.tags') }}
              </h2>
              <tags-tree
                ref="tagsTree"
                v-on:onChange="onUpdateTags"
                v-on:onDelete="onDeleteTags"
                v-on:onChecked="onCheckedTags" />
            </div>
          </grid-col>
          <grid-col
            col-size="4">
            <div>
              <h2
                class="margin-bottom-l">
                <font-awesome-icon
                  class="icon"
                  :icon="icon.variables"/>
                {{ $t('variables.variables') }}
              </h2>
              <safe-router-link
                to="/tags">
                <btn-pill
                  btn-type="secondary"
                  btn-size="s"
                  @click="goTags()">
                  {{ $t('variables.editionTitle') }}
                </btn-pill>
              </safe-router-link>
              <variable-table
                v-if="isSuperAdmin()"
                :data="getData"
                v-on:onRetrieveVariables="retrieveVariablesPage"
                v-on:onEdit="onEditVariable"/>
              <user-variable-table
                v-else
                :data="getData"
                v-on:onRetrieveVariables="retrieveVariablesPage"
                v-on:onEdit="onEditVariable"/>
            </div>
          </grid-col>
        </grid-row>
        <!-- Delete confirmatin dialog -->
        <ok-cancel-modal
          v-if="showConfirmationDelete"
          v-on:ok="deleteTags"
          v-on:cancel="showConfirmationDelete = false"
          :title="$t('commons.delete')"
          :okButton="$t('commons.yes')"
          :cancelButton="$t('commons.cancel')">
          <p slot="body">
            {{ $t('variables.deleteModal') }}
          </p>
        </ok-cancel-modal>
        <!-- Update confirmatin dialog -->
        <ok-cancel-modal
          v-if="showConfirmationUpdate"
          v-on:ok="updateTags"
          v-on:cancel="cancelUpdateTags"
          :title="$t('commons.update')"
          :okButton="$t('commons.yes')"
          :cancelButton="$t('commons.cancel')">
          <p slot="body">
            {{ $t('variables.updateModal') }}
          </p>
        </ok-cancel-modal>
        <accept-modal
          v-if="visibleErrorModal"
          v-on:accept="visibleErrorModal = false"
          :title="messageErrorModal">
        </accept-modal>
      </div>
    </main-layout>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import VariableTable from '@/components/variable/VariableTable';
import UserVariableTable from '@/components/variable/UserVariableTable';
import TagsTree from '@/components/variable/TagsTree';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTags,
  faChartBar
} from '@fortawesome/pro-regular-svg-icons';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import SafeRouterLink from '@/components/shared/SafeRouterLink';
import Chevron from '@/components/shared/Chevron';
import AcceptModal from '@/components/shared/dialogs/AcceptModal';
import variableApi from '@/api/variable';

export default {
  name: 'VariableTagTree',
  components: {
    SafeRouterLink,
    BtnPill,
    OkCancelModal,
    MainLayout,
    GridCol,
    GridRow,
    TagsTree,
    VariableTable,
    FontAwesomeIcon,
    Chevron,
    AcceptModal,
    UserVariableTable
  },
  data () {
    return {
      origin: 'ExplorerEvolutionVariables',
      showConfirmationDelete: false,
      showConfirmationUpdate: false,
      deleteEvent: null,
      updateEvent: null,
      tagIds: null,
      icon: {
        tags: faTags,
        variables: faChartBar
      },
      visibleErrorModal: false,
      messageErrorModal: '',
      variableData: {},
      filter: '',
      page: 1,
      sort: {
        order: '',
        fieldName: ''
      },
      tablePageSize: 10
    };
  },
  watch: {
    getTagsTreeState: function (tree) {
      this.$refs.tagsTree.setModel(tree);
    },
    tagIds: {
      handler () {
        this.apiCall();
      },
      deep: true
    }
  },
  created () {
    const origin = this.$route.query.origin;
    if (origin) {
      this.origin = origin;
    }

    this.retrieveTagsTree({ context: this });
    this.retrieveAggregateVariables({ context: this });
  },
  computed: {
    ...mapGetters([
      'getTagsTreeState',
      'getAggregateGradualVariables',
      'getAggregateOperatedVariables'
    ]),
    getBreadcrumb () {
      switch (this.origin) {
        case 'Workspace':
          return [
            {name: this.$t('menu.workspace'), link: '/workspace'},
            {name: this.$t('variableManagement.title'), link: '/variable-management'},
            {name: this.$t('variables.taxonomy'), link: '/tags-tree', disabled: true}
          ];
        case 'VariableManagement':
          return [
            {name: this.$t('menu.workspace'), link: '/workspace'},
            {name: this.$t('variableManagement.title'), link: '/variable-management'},
            {name: this.$t('variables.taxonomy'), link: '/tags-tree', disabled: true}
          ];
        case 'ExplorerEvolutionVariables':
          return [
            {name: this.$t('menu.explorer'), link: '/explorer-evolution-variables'},
            {name: this.$t('variables.taxonomy'), link: '/tags-tree', disabled: true}
          ];
        default:
          return [];
      }
    },
    getData () {
      return this.variableData;
    }
  },
  methods: {
    ...mapGetters([
      'getUserAccount'
    ]),
    ...mapActions([
      'retrieveTagsTree',
      'retrieveAggregateVariables',
      'updateTagsTree',
      'deleteTagNodes',
      'getAggregateVariables'
    ]),
    goBack () {
      this.$router.push({name: this.origin});
    },
    isSuperAdmin () {
      return this.getUserAccount() &&
        this.getUserAccount().authorities &&
        this.getUserAccount().authorities.includes('ROLE_SUPERADMIN');
    },
    onUpdateTags (event) {
      this.updateEvent = event;
      this.checkVariableLeafTags(event.tree);
    },
    updateTags () {
      this.updateTagsTree({ context: this, tree: this.updateEvent.tree })
        .then(() => this.apiCall());
      this.showConfirmationUpdate = false;
      this.updateEvent = null;
    },
    cancelUpdateTags () {
      this.retrieveTagsTree({ context: this });
      this.showConfirmationUpdate = false;
      this.updateEvent = null;
    },
    onDeleteTags (event) {
      this.deleteEvent = event;
      this.removeIfUsed(event.nodes);
    },
    deleteTags () {
      this.deleteTagNodes({ context: this, nodes: this.deleteEvent.nodes, prevTree: this.deleteEvent.tree })
        .then(() => this.apiCall());
      this.showConfirmationDelete = false;
      this.deleteEvent = null;
    },
    onCheckedTags (ids) {
      if (ids.length === 0) {
        this.tagIds = null;
      } else {
        this.tagIds = ids;
      }
    },
    onEditVariable (variable) {
      if (variable.aggregate === true) {
        const gradual = this.getAggregateGradualVariables.find(aggregateVariable => aggregateVariable.id === variable.id);
        if (gradual) {
          this.$router.push({name: 'AggregateGradualVariable', params: gradual, query: {origin: 'VariableTagTree'}});
        }

        const operated = this.getAggregateOperatedVariables.find(aggregateVariable => aggregateVariable.id === variable.id);
        if (operated) {
          this.$router.push({name: 'AggregateOperatedVariable', params: operated, query: {origin: 'VariableTagTree'}});
        }
      } else {
        this.$router.push({name: 'RawVariable', params: variable, query: {origin: 'VariableTagTree'}});
      }
    },
    checkVariableLeafTags (nodes) {
      let flattenTags = this.flatten(nodes.slice());
      let tagsToCheck = flattenTags.filter(node => node.hasChildren).map(n => n.data.id);
      variableApi.hasTaggedVariables(this, tagsToCheck.join(), false).then(data => {
        if (data.body === true && tagsToCheck.length > 0) {
          this.showConfirmationUpdate = true;
        } else {
          this.updateTags();
          if (event.repeatedTags && event.repeatedTags.length > 0) {
            const message = this.$t('variables.repeatedTags', {tags: event.repeatedTags.join(',')});
            this.showErrorModal(message);
          }
        }
      });
    },
    removeIfUsed (nodes) {
      let flattenTags = this.flatten(nodes[0].children.slice());
      flattenTags = flattenTags.map(node => node.data.id);
      flattenTags.push(nodes[0].data.id);
      variableApi.hasTaggedVariables(this, flattenTags.join(), false).then(data => {
        if (data.body === true) {
          this.showConfirmationDelete = true;
        } else {
          this.deleteTags();
        }
      });
    },
    flatten (data) {
      return data.reduce((r, {children, ...rest}) => {
        const hasChildren = children && children.length > 0;
        r.push({hasChildren: hasChildren, ...rest});
        if (children) r.push(...this.flatten(children));
        return r;
      }, []);
    },
    goTags () {
      this.$router.push({name: 'VariableTag', query: {origin: this.origin}});
    },
    showErrorModal (message) {
      this.messageErrorModal = message;
      this.visibleErrorModal = true;
    },
    retrieveVariablesPage ({page, filter, sort}) {
      if (this.page !== page || this.filter !== filter || sort.fieldName !== this.sort.fieldName || sort.order !== this.sort.order) {
        this.page = page || 1;
        this.filter = filter;
        this.sort.fieldName = sort.fieldName;
        this.sort.order = sort.order;
        this.apiCall();
      }
    },
    apiCall () {
      const tagArray = this.tagIds !== null ? this.tagIds.join() : '';
      variableApi.getPaginatedVariables(this, this.page, this.filter.toUpperCase(), this.sort.fieldName, this.sort.order, tagArray)
        .then(data => {
          this.variableData = {
            data: data.body.variables,
            pagination: {
              totalPages: data.body.totalPages,
              currentPage: data.body.currentPage,
              count: data.body.count
            }
          };
        });
    }
  }
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  h2 {
    color: $jamaicol;
  }

</style>



// WEBPACK FOOTER //
// src/layouts/variables/VariableTagTree.vue